import { Icon } from '../../../widgets/icon';
import { InputDialog, useDialogManager } from '../../../widgets/dialog';
import { SearchInput } from '../../../widgets/input';
import { LeftSidebarLayout } from '../../../widgets/layouts';
import { PageHeader } from '../../../widgets/pageHeader';
import { showErrorNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import { useDropdown } from '../../../widgets/hooks/useDropdown';
import React, { FC, Suspense, useCallback, useState, useTransition } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { T } from 'src/shared/helpers';
import AddIcon from 'src/shared/material_icons/content/add_circle.svg';
import SortIcon from 'src/shared/material_icons/content/sort.svg';
import styled from 'styled-components';
import { DropdownMenuItem } from '../components/shared/sharedStyles';
import { toLocalID } from 'src/shared/helpers';
import { AIAssetLibrarySortBy, SortDirection } from './__generated__/assetLibraryQueriesAIAssetLibraryCategoriesQuery.graphql';
import { assetLibraryQueriesAIAssetLibraryAssetsQuery } from './__generated__/assetLibraryQueriesAIAssetLibraryAssetsQuery.graphql';
import { assetLibraryQueriesCreateAIAssetLibraryCategoryMutation } from './__generated__/assetLibraryQueriesCreateAIAssetLibraryCategoryMutation.graphql';
import { AssetCategoriesTree } from './assetCategoriesTree';
import { AssetGridSkeleton } from './assetGridSkeleton';
import { aiAssetLibraryAssetsQuery, createAIAssetLibraryCategoryMutation } from './assetLibraryQueries';
import { AssetsGrid } from './assetsGrid';
import { CategoriesSkeleton } from './categoriesSkeleton';

interface SortOption {
    label: string;
    sortBy: AIAssetLibrarySortBy;
    sortDirection: SortDirection;
}

const SORT_OPTIONS: SortOption[] = [
    { label: T._('Name A-Z', 'Admin'), sortBy: 'NAME', sortDirection: 'ASC' },
    { label: T._('Name Z-A', 'Admin'), sortBy: 'NAME', sortDirection: 'DESC' },
    { label: T._('Newest first', 'Admin'), sortBy: 'CREATED_DATE', sortDirection: 'DESC' },
    { label: T._('Oldest first', 'Admin'), sortBy: 'CREATED_DATE', sortDirection: 'ASC' }
];

interface AssetLibraryProps {
    /** Callback when user wants to use an asset as input for a tool */
    onUseAsInput?: (imageUrl: string) => void;
    standalone?: boolean;
}

const Container = styled.div`
    display: flex;
    flex: 1;
    background: ${(props) => props.theme.app.backgroundColorBase};
    min-height: 0;
`;

const StandaloneContainer = styled.div`
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    background: transparent;
`;

const Sidebar = styled.div`
    width: 300px;
    flex-shrink: 0;
    background: white;
    border-right: 1px solid ${(props) => props.theme.app.borderColor};
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

const SidebarHeader = styled.div`
    padding: 20px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const HeaderTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    margin: 0;
`;

const HeaderButton = styled(Icon)`
    cursor: pointer;
    width: 24px;
    height: 24px;
    color: ${(props) => props.theme.app.primary};
`;

const SearchSortRow = styled.div`
    padding: 10px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;

const SearchInputWrapper = styled.div`
    flex: 1;
`;

const SortButtonWrapper = styled.div`
    position: relative;
`;

const SortIconButton = styled(Icon)<{ $active?: boolean }>`
    cursor: pointer;
    width: 24px;
    height: 24px;
    color: ${(props) => (props.$active ? props.theme.app.primary : props.theme.app.textColor)};
    opacity: ${(props) => (props.$active ? 1 : 0.6)};

    &:hover {
        opacity: 1;
    }
`;

const SortMenuItem = styled(DropdownMenuItem)<{ $active?: boolean }>`
    font-weight: ${(props) => (props.$active ? 600 : 400)};
    color: ${(props) => (props.$active ? props.theme.app.primary : props.theme.app.textColor)};
`;

const SidebarContent = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
`;

const MainContent = styled.div<{ $isPending?: boolean }>`
    display: flex;
    flex: 1;
    flex-direction: column;
    opacity: ${(props) => (props.$isPending ? 0.5 : 1)};
    transition: opacity 0.2s ease-in-out;
    min-height: 0;
`;

const AssetsGridWithQuery: FC<{
    selectedCategoryId: string | null;
    searchName: string;
    sortBy: AIAssetLibrarySortBy;
    sortDirection: SortDirection;
    onUseAsInput?: (imageUrl: string) => void;
    toolbarRight?: React.ReactNode;
    standalone?: boolean;
}> = ({ selectedCategoryId, searchName, sortBy, sortDirection, onUseAsInput, toolbarRight, standalone }) => {
    const data = useLazyLoadQuery<assetLibraryQueriesAIAssetLibraryAssetsQuery>(aiAssetLibraryAssetsQuery, {
        categoryID: selectedCategoryId ? toLocalID(selectedCategoryId) : undefined,
        searchName: searchName || undefined,
        sortBy,
        sortDirection
    });

    return <AssetsGrid query={data} selectedCategoryId={selectedCategoryId} onUseAsInput={onUseAsInput} toolbarRight={toolbarRight} standalone={standalone} />;
};

export const AssetLibrary: FC<AssetLibraryProps> = ({ onUseAsInput, standalone = false }) => {
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [isPending, startTransition] = useTransition();
    const { showDialog, closeDialog } = useDialogManager();
    const [categoriesRefetchKey, setCategoriesRefetchKey] = useState(0);

    // Categories search & sort
    const [categorySearchValue, setCategorySearchValue] = useState('');
    const [categorySearch, setCategorySearch] = useState('');
    const [categorySortBy, setCategorySortBy] = useState<AIAssetLibrarySortBy>('CREATED_DATE');
    const [categorySortDirection, setCategorySortDirection] = useState<SortDirection>('DESC');

    // Assets search & sort
    const [assetSearchValue, setAssetSearchValue] = useState('');
    const [assetSearch, setAssetSearch] = useState('');
    const [assetSortBy, setAssetSortBy] = useState<AIAssetLibrarySortBy>('CREATED_DATE');
    const [assetSortDirection, setAssetSortDirection] = useState<SortDirection>('DESC');

    // Dropdown hooks for sort menus
    const [setCatSortOpen, setCatSortClose, isCatSortOpen, CatSortDropdown] = useDropdown();
    const [setAssetSortOpen, setAssetSortClose, isAssetSortOpen, AssetSortDropdown] = useDropdown();

    const [createCategory] = useMutation<assetLibraryQueriesCreateAIAssetLibraryCategoryMutation>(createAIAssetLibraryCategoryMutation);

    const handleCategorySelect = (categoryId: string | null) => {
        startTransition(() => {
            setSelectedCategoryId(categoryId);
        });
    };

    const handleCategorySortSelect = useCallback(
        (option: SortOption) => {
            startTransition(() => {
                setCategorySortBy(option.sortBy);
                setCategorySortDirection(option.sortDirection);
            });
            setCatSortClose();
        },
        [setCatSortClose, startTransition]
    );

    const handleAssetSortSelect = useCallback(
        (option: SortOption) => {
            startTransition(() => {
                setAssetSortBy(option.sortBy);
                setAssetSortDirection(option.sortDirection);
            });
            setAssetSortClose();
        },
        [setAssetSortClose, startTransition]
    );

    const handleAddCategory = useCallback(() => {
        showDialog(
            'add-category',
            <InputDialog
                label={T._('Category name', 'Admin')}
                defaultValue=''
                required
                onConfirm={(name) => {
                    closeDialog('add-category');
                    createCategory({
                        variables: { name: name.trim(), sortOrder: 0 },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Category created.', 'Admin') });
                            setCategoriesRefetchKey((k) => k + 1);
                        },
                        onError: (error) => {
                            console.error('Failed to create category:', error);
                            showErrorNotification({ message: T._('Failed creating new category', 'Admin') });
                        }
                    });
                }}
            />
        );
    }, [showDialog, closeDialog, createCategory]);

    const isCatSortActive = categorySortBy !== 'CREATED_DATE' || categorySortDirection !== 'DESC';
    const isAssetSortActive = assetSortBy !== 'CREATED_DATE' || assetSortDirection !== 'DESC';

    const categoriesTree = (
        <Suspense fallback={<CategoriesSkeleton />}>
            <AssetCategoriesTree
                selectedCategoryId={selectedCategoryId}
                onCategorySelect={handleCategorySelect}
                searchName={categorySearch}
                sortBy={categorySortBy}
                sortDirection={categorySortDirection}
                externalRefetchKey={categoriesRefetchKey}
                onAddCategory={handleAddCategory}
                standalone={standalone}
            />
        </Suspense>
    );

    if (standalone) {
        return (
            <StandaloneContainer>
                <LeftSidebarLayout header={<PageHeader pageKey='ai-studio-library' title={T._('Library', 'Admin')} />} sidebar={categoriesTree}>
                    <Suspense fallback={<AssetGridSkeleton />}>
                        <AssetsGridWithQuery
                            selectedCategoryId={selectedCategoryId}
                            searchName={assetSearch}
                            sortBy={assetSortBy}
                            sortDirection={assetSortDirection}
                            onUseAsInput={onUseAsInput}
                            standalone
                            toolbarRight={
                                <>
                                    <SearchInput
                                        placeholder={T._('Search assets...', 'Admin')}
                                        value={assetSearchValue}
                                        onChange={(e) => setAssetSearchValue(e.currentTarget.value)}
                                        onSearch={(filter) => startTransition(() => setAssetSearch(filter))}
                                    />
                                    <SortButtonWrapper>
                                        <SortIconButton
                                            $active={isAssetSortActive}
                                            $hoverable
                                            tooltip={T._('Sort', 'Admin')}
                                            onClick={(e: React.MouseEvent) =>
                                                isAssetSortOpen ? setAssetSortClose() : setAssetSortOpen(e.currentTarget as HTMLElement, 'bottom', 'right')
                                            }
                                        >
                                            <SortIcon />
                                        </SortIconButton>
                                        {isAssetSortOpen && (
                                            <AssetSortDropdown>
                                                {SORT_OPTIONS.map((option) => (
                                                    <SortMenuItem
                                                        key={`${option.sortBy}-${option.sortDirection}`}
                                                        $active={assetSortBy === option.sortBy && assetSortDirection === option.sortDirection}
                                                        onClick={() => handleAssetSortSelect(option)}
                                                    >
                                                        {option.label}
                                                    </SortMenuItem>
                                                ))}
                                            </AssetSortDropdown>
                                        )}
                                    </SortButtonWrapper>
                                </>
                            }
                        />
                    </Suspense>
                </LeftSidebarLayout>
            </StandaloneContainer>
        );
    }

    return (
        <Container>
            <Sidebar>
                <SidebarHeader>
                    <HeaderTitle>{T._('Categories', 'Admin')}</HeaderTitle>
                    <HeaderButton $hoverable tooltip={T._('Add a new category', 'Admin')} onClick={handleAddCategory}>
                        <AddIcon />
                    </HeaderButton>
                </SidebarHeader>
                <SearchSortRow>
                    <SearchInputWrapper>
                        <SearchInput
                            fullWidth
                            placeholder={T._('Search categories...', 'Admin')}
                            value={categorySearchValue}
                            onChange={(e) => setCategorySearchValue(e.currentTarget.value)}
                            onSearch={(filter) => {
                                startTransition(() => {
                                    setCategorySearch(filter);
                                });
                            }}
                        />
                    </SearchInputWrapper>
                    <SortButtonWrapper>
                        <SortIconButton
                            $active={isCatSortActive}
                            $hoverable
                            tooltip={T._('Sort', 'Admin')}
                            onClick={(e: React.MouseEvent) =>
                                isCatSortOpen ? setCatSortClose() : setCatSortOpen(e.currentTarget as HTMLElement, 'bottom', 'right')
                            }
                        >
                            <SortIcon />
                        </SortIconButton>
                        {isCatSortOpen && (
                            <CatSortDropdown>
                                {SORT_OPTIONS.map((option) => (
                                    <SortMenuItem
                                        key={`${option.sortBy}-${option.sortDirection}`}
                                        $active={categorySortBy === option.sortBy && categorySortDirection === option.sortDirection}
                                        onClick={() => handleCategorySortSelect(option)}
                                    >
                                        {option.label}
                                    </SortMenuItem>
                                ))}
                            </CatSortDropdown>
                        )}
                    </SortButtonWrapper>
                </SearchSortRow>
                <SidebarContent>{categoriesTree}</SidebarContent>
            </Sidebar>
            <MainContent $isPending={isPending}>
                <Suspense fallback={<AssetGridSkeleton />}>
                    <AssetsGridWithQuery
                        selectedCategoryId={selectedCategoryId}
                        searchName={assetSearch}
                        sortBy={assetSortBy}
                        sortDirection={assetSortDirection}
                        onUseAsInput={onUseAsInput}
                        toolbarRight={
                            <>
                                <SearchInput
                                    fullWidth
                                    placeholder={T._('Search assets...', 'Admin')}
                                    value={assetSearchValue}
                                    onChange={(e) => setAssetSearchValue(e.currentTarget.value)}
                                    onSearch={(filter) => {
                                        startTransition(() => {
                                            setAssetSearch(filter);
                                        });
                                    }}
                                />
                                <SortButtonWrapper>
                                    <SortIconButton
                                        $active={isAssetSortActive}
                                        $hoverable
                                        tooltip={T._('Sort', 'Admin')}
                                        onClick={(e: React.MouseEvent) =>
                                            isAssetSortOpen ? setAssetSortClose() : setAssetSortOpen(e.currentTarget as HTMLElement, 'bottom', 'right')
                                        }
                                    >
                                        <SortIcon />
                                    </SortIconButton>
                                    {isAssetSortOpen && (
                                        <AssetSortDropdown>
                                            {SORT_OPTIONS.map((option) => (
                                                <SortMenuItem
                                                    key={`${option.sortBy}-${option.sortDirection}`}
                                                    $active={assetSortBy === option.sortBy && assetSortDirection === option.sortDirection}
                                                    onClick={() => handleAssetSortSelect(option)}
                                                >
                                                    {option.label}
                                                </SortMenuItem>
                                            ))}
                                        </AssetSortDropdown>
                                    )}
                                </SortButtonWrapper>
                            </>
                        }
                    />
                </Suspense>
            </MainContent>
        </Container>
    );
};
